import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchLeads } from "@/lib/api";
import { Compass, ArrowLeft, RefreshCw, Search, LogOut, Inbox, DollarSign, Send } from "lucide-react";
import { CrmDeliveries } from "@/components/admin/CrmDeliveries";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import SEO from "@/components/SEO";
import PricingCMS from "@/components/PricingCMS";
import { useAuth } from "@/lib/AuthContext";

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("leads");
  const { user, logout } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      setLeads(await fetchLeads());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const formatIST = (iso) => {
    if (!iso) return "—";
    const p = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
      }).formatToParts(new Date(iso)).map((x) => [x.type, x.value])
    );
    return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute} ${p.dayPeriod}`;
  };

  const filtered = leads.filter((l) => {
    const t = `${l.name || ""} ${l.destination || ""} ${l.email || ""} ${l.phone || ""}`.toLowerCase();
    return t.includes(query.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-[#0f1512] text-white" data-testid="admin-page">
      <SEO title="Seller Desk | Vacations Pro" description="Internal seller dashboard." path="/admin" noindex />
      {/* Header */}
      <header className="border-b border-white/10 px-6 md:px-10 py-5 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white text-xs tracking-[0.2em] uppercase">
            <ArrowLeft className="w-3.5 h-3.5" /> Home
          </Link>
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5" strokeWidth={1.25} />
            <span className="vp-label text-white tracking-[0.28em]">VACATIONS PRO · SELLER DESK</span>
          </div>
        </div>
        <div className="flex items-center gap-5 flex-wrap">
          <button
            onClick={load}
            className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-white/70 hover:text-white"
            data-testid="refresh-leads-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          {user && (
            <div className="flex items-center gap-2 text-xs text-white/60" data-testid="admin-user-chip">
              {user.picture && <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />}
              <span className="hidden md:inline">{user.email}</span>
            </div>
          )}
          <button
            onClick={async () => { await logout(); window.location.href = "/login"; }}
            className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-white/70 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <div className="px-6 md:px-10 py-10 pb-32 md:pb-40">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-white/10" data-testid="admin-tabs">
          <button
            onClick={() => setTab("leads")}
            className={`flex items-center gap-2 px-4 py-3 text-xs tracking-[0.18em] uppercase border-b-2 transition-colors ${
              tab === "leads"
                ? "border-[var(--vp-accent)] text-white"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
            data-testid="admin-tab-leads"
          >
            <Inbox className="w-3.5 h-3.5" /> Leads
          </button>
          <button
            onClick={() => setTab("pricing")}
            className={`flex items-center gap-2 px-4 py-3 text-xs tracking-[0.18em] uppercase border-b-2 transition-colors ${
              tab === "pricing"
                ? "border-[var(--vp-accent)] text-white"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
            data-testid="admin-tab-pricing"
          >
            <DollarSign className="w-3.5 h-3.5" /> Pricing CMS
          </button>
          <button
            onClick={() => setTab("crm")}
            className={`flex items-center gap-2 px-4 py-3 text-xs tracking-[0.18em] uppercase border-b-2 transition-colors ${
              tab === "crm"
                ? "border-[var(--vp-accent)] text-white"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
            data-testid="admin-tab-crm"
          >
            <Send className="w-3.5 h-3.5" /> CRM deliveries
          </button>
        </div>

        {tab === "pricing" ? (
          <PricingCMS />
        ) : tab === "crm" ? (
          <CrmDeliveries />
        ) : (
          <>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="vp-label text-white/60 mb-2 block">INBOX</span>
            <h1 className="font-serif-vp text-4xl md:text-5xl">Captured Leads</h1>
            <p className="text-white/60 mt-2 text-sm">{leads.length} total · freshest on top</p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              placeholder="Search name, destination…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 pr-4 py-2.5 bg-white/5 border border-white/15 rounded-md text-sm outline-none focus:border-[var(--vp-accent)] w-64"
              data-testid="lead-search-input"
            />
          </div>
        </div>

        <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="admin-lead-table">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/60">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Traveller</th>
                <th className="text-left px-4 py-3 font-medium">Destination</th>
                <th className="text-left px-4 py-3 font-medium">Travel Date</th>
                <th className="text-left px-4 py-3 font-medium">Pax</th>
                <th className="text-left px-4 py-3 font-medium">Contact</th>
                <th className="text-left px-4 py-3 font-medium">Preferences</th>
                <th className="text-left px-4 py-3 font-medium">Received</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-t border-white/5">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-4 py-4"><Skeleton className="h-4 w-full bg-white/10" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-white/50">
                    <div className="font-serif-vp text-3xl mb-2 text-white/80">No leads yet</div>
                    <div className="text-sm">Captured conversations from Ayla will appear here.</div>
                  </td>
                </tr>
              ) : (
                filtered.map((l) => (
                  <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-4">
                      <div className="font-medium">{l.name || <span className="text-white/40">—</span>}</div>
                    </td>
                    <td className="px-4 py-4 text-white/80">{l.destination || "—"}</td>
                    <td className="px-4 py-4 text-white/80">{l.travel_date || "—"}</td>
                    <td className="px-4 py-4 text-white/80">{l.persons ?? "—"}</td>
                    <td className="px-4 py-4 text-white/80">
                      {l.email && <div>{l.email}</div>}
                      {l.phone && <div className="text-white/60">{l.phone}</div>}
                      {!l.email && !l.phone && "—"}
                    </td>
                    <td className="px-4 py-4 text-white/70 max-w-xs truncate">{l.preferences || "—"}</td>
                    <td className="px-4 py-4 text-white/60 whitespace-nowrap" data-testid={`lead-received-${l.id}`}>
                      {formatIST(l.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      <Badge className="bg-[var(--vp-accent)]/20 text-[var(--vp-accent)] border border-[var(--vp-accent)]/30 hover:bg-[var(--vp-accent)]/20">
                        {l.status}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
